// Build and download a traceability ZIP for a finalised OT:
//   <OT number>/
//     Informations_OT.xlsx         (OT-level header, lots, containers,
//                                   BL versions, slot docs, redevances,
//                                   photos, empotage files)
//     Documents/<stage>/...        (slot docs)
//     BL_Provisoires/...           (draft BL versions)
//     Photos/...                   (stuffing photos)
//     Empotage/...                 (rapport / ticket / other)
//     Redevances/...               (levy receipts)
//     CCC/<lot>/...                (per-lot CCC docs)
//     Livraisons/<bl-or-shipping>/ (per-delivery folder, see below)
//
// Each delivery folder carries its own focused workbook + a GeoJSON file
// of the parcels feeding into it — buyers and auditors who only care
// about one shipment can grab the whole folder without sifting through
// merged sheets.
//
// File binaries are pulled via the OT's presigned-download endpoint —
// same flow as documents-side-panel's bulk download. We swallow per-
// file failures and surface a single partial-failure count so one bad
// fetch doesn't take the whole bundle down.

import type JSZip from "jszip"

import {
  getDownloadUrl,
  getTraceability,
  getTraceabilityGeo,
} from "@/features/transit/api/transit.api"
import {
  buildTraceabilitySnapshot,
  type TraceabilitySnapshot,
} from "@/features/transit/lib/traceability"
import type {
  EmpotageFileItem,
  OT,
  OTDraftBL,
  OTTraceability,
  OTTraceabilityGeo,
  ParcelGeoEntry,
  TraceabilityDeliveryDigest,
  TraceabilityParcelEntry,
  TraceabilityProducerEntry,
  TraceabilityTransactionEntry,
} from "@/features/transit/api/types"

export interface BundleResult {
  totalFiles: number
  failedFiles: number
}

export async function exportTraceabilityBundle(
  ot: OT,
  // Consolidated traceability roll-up. Optional — when omitted, the
  // bundle fetches it inline so the workbook always carries the
  // EUDR/producer/parcel sheets. We don't trust the caller to have it
  // cached: react-query's `enabled`/`refetch` race with the click
  // handler made this brittle in practice.
  traceability: OTTraceability | undefined,
  // Translator for stage-folder names. Caller passes its own scoped `t`
  // so we don't reach into i18next here.
  translateStage: (stage: OT["stage"]) => string,
): Promise<BundleResult> {
  const [{ default: JSZip }, XLSX, resolvedTraceability, resolvedGeo] =
    await Promise.all([
      import("jszip"),
      import("xlsx"),
      traceability
        ? Promise.resolve(traceability)
        : getTraceability(ot.id).catch((err: unknown) => {
            // A failed traceability fetch shouldn't kill the whole
            // bundle — log and ship the docs without the sheets.
            console.warn("[traceability-bundle] traceability fetch failed", err)
            return undefined
          }),
      getTraceabilityGeo(ot.id).catch((err: unknown) => {
        console.warn("[traceability-bundle] geo fetch failed", err)
        return undefined
      }),
    ])

  const snapshot = buildTraceabilitySnapshot(ot)
  const zip = new JSZip()
  const root = zip.folder(snapshot.ot.otNumber) ?? zip

  root.file(
    "Informations_OT.xlsx",
    buildOTWorkbook(ot, snapshot, XLSX),
  )

  const usedNames = new Map<string, number>()
  const tasks: Array<Promise<void>> = []

  for (const doc of ot.docs ?? []) {
    tasks.push(
      addRemoteFile(root, ot.id, doc.storedKey, doc.name, usedNames, [
        "Documents",
        translateStage(doc.stage),
      ]),
    )
  }
  for (const bl of ot.draftBLs ?? []) {
    tasks.push(
      addRemoteFile(root, ot.id, bl.storedKey, draftBLName(bl), usedNames, [
        "BL_Provisoires",
      ]),
    )
  }
  for (const photo of ot.photos ?? []) {
    tasks.push(
      addRemoteFile(root, ot.id, photo.storedKey, photo.name, usedNames, [
        "Photos",
      ]),
    )
  }
  for (const file of ot.empotageFiles ?? []) {
    tasks.push(
      addRemoteFile(root, ot.id, file.storedKey, file.name, usedNames, [
        "Empotage",
        empotageSubfolder(file),
      ]),
    )
  }
  for (const redevance of ot.redevances ?? []) {
    tasks.push(
      addRemoteFile(
        root,
        ot.id,
        redevance.storedKey,
        redevance.name,
        usedNames,
        ["Redevances"],
      ),
    )
  }
  for (const link of ot.lots ?? []) {
    const lotFolder = sanitize(
      link.lot?.lotNumber ? link.lot.lotNumber : `lot-${link.lotId}`,
    )
    for (const ccc of link.cccDocs ?? []) {
      tasks.push(
        addRemoteFile(root, ot.id, ccc.storedKey, ccc.name, usedNames, [
          "CCC",
          lotFolder,
        ]),
      )
    }
  }

  // Per-delivery breakout. Each delivery gets a folder containing its
  // own workbook (delivery header + scoped producers / parcels / receipts)
  // and a GeoJSON file with the parcels' polygons. Operators told us the
  // global trace sheets were too dense to be useful — the per-delivery
  // folder lets the recipient pull exactly the slice they care about.
  if (resolvedTraceability) {
    writeDeliveryFolders(root, resolvedTraceability, resolvedGeo, XLSX)
  }

  const totalFiles = tasks.length
  const settled = await Promise.allSettled(tasks)
  const failedFiles = settled.filter((s) => s.status === "rejected").length

  const blob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  })
  triggerDownload(blob, `${snapshot.ot.otNumber}-traceabilite.zip`)

  return { totalFiles, failedFiles }
}

// ── OT-level workbook ────────────────────────────────────────────────

type XLSXModule = typeof import("xlsx")

function buildOTWorkbook(
  ot: OT,
  snapshot: TraceabilitySnapshot,
  XLSX: XLSXModule,
): ArrayBuffer {
  const wb = XLSX.utils.book_new()

  appendSheet(wb, XLSX, "Informations OT", [
    ["Numéro d'ordre", snapshot.ot.otNumber],
    ["Libellé", snapshot.ot.label],
    ["Étape", snapshot.ot.stage],
    ["Archivé", boolFr(snapshot.ot.archived)],
    ["Finalisé le", isoToDate(snapshot.ot.completedAt)],
    ["Client", snapshot.ot.clientName],
    ["Contrat", snapshot.ot.contractNumber ?? `#${snapshot.ot.contractId}`],
    ["Certifications", snapshot.ot.certifications.join(", ") || "—"],
    ["Transitaire", snapshot.shipment.freightForwarder ?? "—"],
    ["Référence booking", snapshot.shipment.bookingRef ?? "—"],
    ["Navire", snapshot.shipment.vessel ?? "—"],
    ["Voyage", snapshot.shipment.voyage ?? "—"],
    ["Port de chargement", snapshot.shipment.pol ?? "—"],
    ["Port de déchargement", snapshot.shipment.pod ?? "—"],
    ["ETD", isoToDate(snapshot.shipment.etd)],
    ["N° SAIGIC", snapshot.shipment.saigicNumber ?? "—"],
    ["Fumigation autorisée", boolFr(snapshot.shipment.fumigationAllowed)],
    ["Lots", snapshot.totals.lotCount],
    ["Sacs", snapshot.totals.totalBags],
    ["Poids net (MT)", round3(snapshot.totals.totalNetMT)],
    [
      "Poids empoté (MT)",
      snapshot.totals.totalStuffedWeightKg != null
        ? round3(snapshot.totals.totalStuffedWeightKg / 1000)
        : "—",
    ],
    [
      "Poids BL final (MT)",
      snapshot.totals.finalBLWeightKg != null
        ? round3(snapshot.totals.finalBLWeightKg / 1000)
        : "—",
    ],
  ])

  appendObjectsSheet(wb, XLSX, "Lots", snapshot.lots.map((lot) => ({
    "ID lot": lot.lotId,
    "Numéro de lot": lot.lotNumber,
    PID: lot.pid ?? "—",
    Sacs: lot.bags,
    "Poids net (kg)": lot.netKg,
    "Poids net (MT)": round3(lot.netMT),
    Usinage: lot.usinageNumber ?? "—",
    "Décision CCC": lot.cccKind ?? "—",
    "N° BV": lot.cccBvNumber ?? "—",
    "Délivré le": isoToDate(lot.cccIssuedAt),
  })))

  appendObjectsSheet(wb, XLSX, "Conteneurs", (ot.containers ?? []).map((c) => ({
    "ID conteneur": c.id,
    "Numéro": c.containerNumber,
    "N° plomb": c.plombNumber,
    "Date d'empotage": isoToDate(c.stuffingDate),
    "Poids pesée (kg)": c.weighingWeightKg ?? "—",
  })))

  const sortedDraftBLs: OTDraftBL[] = [...(ot.draftBLs ?? [])].sort(
    (a, b) => a.versionIdx - b.versionIdx,
  )
  appendObjectsSheet(wb, XLSX, "BL provisoires", sortedDraftBLs.map((bl) => ({
    Version: bl.versionIdx,
    Nom: bl.name,
    "Poids (kg)": bl.weightKg,
    "Téléversé le": isoToDateTime(bl.uploadedAt),
    "Téléversé par": bl.uploadedBy ?? "—",
    Note: bl.note ?? "",
  })))

  appendObjectsSheet(wb, XLSX, "Documents", (ot.docs ?? []).map((d) => ({
    Étape: d.stage,
    Emplacement: d.slotId,
    Nom: d.name,
    "Téléversé le": isoToDateTime(d.uploadedAt),
    "Téléversé par": d.uploadedBy ?? "—",
    Note: d.note ?? "",
  })))

  appendObjectsSheet(wb, XLSX, "Redevances", (ot.redevances ?? []).map((r) => ({
    Référence: r.reference ?? "—",
    "Montant (FCFA)": r.amountFcfa ?? "—",
    Nom: r.name,
    "Téléversé le": isoToDateTime(r.uploadedAt),
    "Téléversé par": r.uploadedBy ?? "—",
    Note: r.note ?? "",
  })))

  appendObjectsSheet(wb, XLSX, "Photos empotage", (ot.photos ?? []).map((p) => ({
    Nom: p.name,
    "Téléversé le": isoToDateTime(p.uploadedAt),
    "Téléversé par": p.uploadedBy ?? "—",
    Note: p.note ?? "",
  })))

  appendObjectsSheet(
    wb,
    XLSX,
    "Documents empotage",
    (ot.empotageFiles ?? []).map((f) => ({
      Type: empotageKindLabel(f),
      Nom: f.name,
      "Téléversé le": isoToDateTime(f.uploadedAt),
      "Téléversé par": f.uploadedBy ?? "—",
      Note: f.note ?? "",
    })),
  )

  return XLSX.write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer
}

// ── Per-delivery breakout ────────────────────────────────────────────

function writeDeliveryFolders(
  root: JSZip,
  traceability: OTTraceability,
  geo: OTTraceabilityGeo | undefined,
  XLSX: XLSXModule,
): void {
  const deliveriesFolder = root.folder("Livraisons")
  if (!deliveriesFolder) return

  // Index producers + parcels + geo so the per-delivery slicing is O(1).
  const producerById = new Map<number, TraceabilityProducerEntry>()
  for (const p of traceability.producers) producerById.set(p.id, p)
  const parcelsByFarmer = new Map<number, TraceabilityParcelEntry[]>()
  for (const parcel of traceability.parcels) {
    const bucket = parcelsByFarmer.get(parcel.farmerId) ?? []
    bucket.push(parcel)
    parcelsByFarmer.set(parcel.farmerId, bucket)
  }
  const geoByParcelId = new Map<number, ParcelGeoEntry>()
  for (const g of geo?.parcels ?? []) geoByParcelId.set(g.id, g)

  const transactionsByDelivery = new Map<number, TraceabilityTransactionEntry[]>()
  for (const tx of traceability.transactions) {
    if (tx.deliveryId == null) continue
    const bucket = transactionsByDelivery.get(tx.deliveryId) ?? []
    bucket.push(tx)
    transactionsByDelivery.set(tx.deliveryId, bucket)
  }

  const usedFolders = new Map<string, number>()
  for (const delivery of traceability.deliveries) {
    const folderName = uniqueFolderName(
      usedFolders,
      sanitize(deliveryFolderName(delivery)),
    )
    const folder = deliveriesFolder.folder(folderName)
    if (!folder) continue

    const txs = transactionsByDelivery.get(delivery.id) ?? []
    const producerIds = new Set<number>()
    for (const tx of txs) producerIds.add(tx.producerId)
    const producers = [...producerIds]
      .map((id) => producerById.get(id))
      .filter((p): p is TraceabilityProducerEntry => p != null)
    const parcels: TraceabilityParcelEntry[] = []
    for (const id of producerIds) {
      for (const parcel of parcelsByFarmer.get(id) ?? []) {
        parcels.push(parcel)
      }
    }

    folder.file(
      "Livraison.xlsx",
      buildDeliveryWorkbook(delivery, producers, parcels, txs, XLSX),
    )
    folder.file(
      "Polygones.geojson",
      buildParcelGeoJSON(parcels, geoByParcelId),
    )
  }
}

// Pick a stable folder name for a delivery. Prefer the BL number, then
// the shipping-form number, then the numeric id — anything legible to
// the recipient when they sort the folder by name.
function deliveryFolderName(d: TraceabilityDeliveryDigest): string {
  if (d.billOfLadingNumber) return d.billOfLadingNumber
  if (d.shippingFormNumber) return d.shippingFormNumber
  return `delivery-${d.id}`
}

function buildDeliveryWorkbook(
  delivery: TraceabilityDeliveryDigest,
  producers: TraceabilityProducerEntry[],
  parcels: TraceabilityParcelEntry[],
  transactions: TraceabilityTransactionEntry[],
  XLSX: XLSXModule,
): ArrayBuffer {
  const wb = XLSX.utils.book_new()

  appendSheet(wb, XLSX, "Livraison", [
    ["ID livraison", delivery.id],
    ["N° BL (connaissement)", delivery.billOfLadingNumber ?? "—"],
    ["N° fiche de livraison", delivery.shippingFormNumber ?? "—"],
    ["Coopérative", delivery.cooperativeName ?? "—"],
    ["Date d'expédition", isoToDate(delivery.shippingDate)],
    ["Date d'export", isoToDate(delivery.dateOfExport)],
    ["Transporteur", delivery.carrierName ?? "—"],
    ["Camion", delivery.truckRegistrationNumber ?? "—"],
    ["Poids net (kg)", delivery.netKilogramsAttached ?? "—"],
    ["Sacs", delivery.numberOfBagsAttached ?? "—"],
    ["Verrouillée", boolFr(delivery.isLocked)],
    ["Producteurs", delivery.producerCount],
    ["Reçus d'achat", delivery.purchaseCount],
    ["Traçabilité (%)", delivery.traceabilityStatus],
    ["À risque", boolFr(delivery.hasRisks)],
    ["Conforme", boolFr(delivery.isCompliant)],
  ])

  appendObjectsSheet(wb, XLSX, "Producteurs", producers.map((p) => ({
    "ID producteur": p.id,
    Nom: p.name,
    "ID interne": p.internalFarmerId ?? "—",
    "ID Rhizo": p.rhizoFarmerId ?? "—",
    "Statut conformité": p.complianceStatus ?? "—",
    Certifications: p.certificates.join(", ") || "—",
    Parcelles: p.parcelsCount,
    Traçable: boolFr(p.isTraceable),
    "Conforme EUDR": boolFr(p.isEUDR),
  })))

  appendObjectsSheet(wb, XLSX, "Parcelles", parcels.map((p) => ({
    "ID parcelle": p.id,
    "N° parcelle": p.parcelId ?? "—",
    "ID interne": p.internalId ?? "—",
    "ID producteur": p.farmerId,
    Hectares: p.hectares,
    Centroïde: p.centroid ?? "—",
    Région: p.regionName ?? "—",
    Certifications: p.certifications.join(", ") || "—",
    Statut: p.status,
    "À risque déforestation": boolFr(p.isAtRiskDeforested),
    Conforme: boolFr(p.isCompliant),
  })))

  appendObjectsSheet(wb, XLSX, "Reçus d'achat", transactions.map((tx) => ({
    "ID achat": tx.id,
    "N° reçu": tx.receiptNumber || "—",
    Date: tx.date ?? "—",
    "ID producteur": tx.producerId,
    Producteur: tx.producerName,
    "Réf. producteur": tx.producerRef ?? "—",
    Sacs: tx.numberOfBags,
    "Poids net (kg)": tx.netKilograms,
    "Prix bord champ (CFA/kg)": tx.basePrice,
    "Prix total (CFA)": tx.totalPrice,
    Certifications: tx.certificates.join(", ") || "—",
    "Image disponible": boolFr(tx.hasImage),
  })))

  return XLSX.write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer
}

// GeoJSON FeatureCollection for the parcels feeding one delivery. We
// emit Polygon features when at least 3 surveyed points exist (closing
// the ring with the first point so GIS tools parse it cleanly), and
// fall back to a Point feature for the centroid otherwise.
function buildParcelGeoJSON(
  parcels: TraceabilityParcelEntry[],
  geoByParcelId: Map<number, ParcelGeoEntry>,
): string {
  const features: unknown[] = []
  for (const parcel of parcels) {
    const geo = geoByParcelId.get(parcel.id)
    const properties = {
      id: parcel.id,
      parcelId: parcel.parcelId,
      internalId: parcel.internalId,
      farmerId: parcel.farmerId,
      hectares: parcel.hectares,
      status: parcel.status,
      certifications: parcel.certifications,
      regionName: parcel.regionName,
      isAtRiskDeforested: parcel.isAtRiskDeforested,
      isCompliant: parcel.isCompliant,
    }
    const points = geo?.locations ?? []
    if (points.length >= 3) {
      // GeoJSON uses [lon, lat] and rings close on the first point.
      const ring = points.map((pt) => [pt.longitude, pt.latitude])
      const first = ring[0]
      const last = ring[ring.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]])
      }
      features.push({
        type: "Feature",
        properties,
        geometry: { type: "Polygon", coordinates: [ring] },
      })
      continue
    }
    const centroid = parseCentroid(parcel.centroid)
    if (centroid) {
      features.push({
        type: "Feature",
        properties,
        geometry: {
          type: "Point",
          coordinates: [centroid.longitude, centroid.latitude],
        },
      })
    }
  }
  return JSON.stringify({ type: "FeatureCollection", features }, null, 2)
}

// Centroid is stored as a "lat,lon" string in the trace payload. Be
// tolerant: ignore unparseable values rather than fail the whole bundle.
function parseCentroid(
  raw: string | null,
): { latitude: number; longitude: number } | null {
  if (!raw) return null
  const [latStr, lonStr] = raw.split(",").map((s) => s.trim())
  const lat = Number(latStr)
  const lon = Number(lonStr)
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  return { latitude: lat, longitude: lon }
}

function uniqueFolderName(used: Map<string, number>, name: string): string {
  const count = (used.get(name) ?? 0) + 1
  used.set(name, count)
  return count === 1 ? name : `${name} (${count})`
}

// ── SheetJS helpers ──────────────────────────────────────────────────

// SheetJS won't create empty sheets cleanly. We always emit the header
// row so the recipient can tell "no data" from "missing sheet".
function appendObjectsSheet(
  wb: ReturnType<XLSXModule["utils"]["book_new"]>,
  XLSX: XLSXModule,
  name: string,
  rows: Record<string, unknown>[],
): void {
  const ws = rows.length
    ? XLSX.utils.json_to_sheet(rows)
    : XLSX.utils.aoa_to_sheet([["—"]])
  XLSX.utils.book_append_sheet(wb, ws, name)
}

function appendSheet(
  wb: ReturnType<XLSXModule["utils"]["book_new"]>,
  XLSX: XLSXModule,
  name: string,
  rows: Array<[string, unknown]>,
): void {
  const ws = XLSX.utils.aoa_to_sheet([["Champ", "Valeur"], ...rows])
  XLSX.utils.book_append_sheet(wb, ws, name)
}

// ── Per-file fetcher ─────────────────────────────────────────────────

async function addRemoteFile(
  root: JSZip,
  otId: number,
  storedKey: string,
  name: string,
  usedNames: Map<string, number>,
  folderPath: string[],
): Promise<void> {
  let folder: JSZip | null = root
  for (const part of folderPath) {
    folder = folder?.folder(sanitize(part)) ?? null
  }
  if (!folder) return

  const safeName = uniqueName(usedNames, folderPath.join("/"), sanitize(name))
  const { url } = await getDownloadUrl(otId, storedKey)
  const res = await fetch(url)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  folder.file(safeName, blob)
}

// ── Helpers ──────────────────────────────────────────────────────────

// Strip the chars that are illegal on Windows or unfriendly inside ZIP
// archives. Hyphens, periods, underscores, spaces and Unicode are kept.
function sanitize(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, "_").trim() || "fichier"
}

function uniqueName(
  used: Map<string, number>,
  folder: string,
  name: string,
): string {
  const key = `${folder}/${name}`
  const count = (used.get(key) ?? 0) + 1
  used.set(key, count)
  if (count === 1) return name
  const dot = name.lastIndexOf(".")
  if (dot < 0) return `${name} (${count})`
  return `${name.slice(0, dot)} (${count})${name.slice(dot)}`
}

function draftBLName(bl: OTDraftBL): string {
  if (bl.name.toLowerCase().includes(`v${bl.versionIdx}`)) return bl.name
  const dot = bl.name.lastIndexOf(".")
  if (dot < 0) return `v${bl.versionIdx} - ${bl.name}`
  return `v${bl.versionIdx} - ${bl.name.slice(0, dot)}${bl.name.slice(dot)}`
}

function empotageKindLabel(file: EmpotageFileItem): string {
  switch (file.kind) {
    case "rapportEmpotage":
      return "Rapport d'empotage"
    case "ticketPesee":
      return "Ticket de pesée"
    case "other":
    default:
      return "Autre"
  }
}

function empotageSubfolder(file: EmpotageFileItem): string {
  switch (file.kind) {
    case "rapportEmpotage":
      return "Rapport"
    case "ticketPesee":
      return "Tickets_Pesee"
    default:
      return "Autres"
  }
}

function boolFr(v: boolean): string {
  return v ? "Oui" : "Non"
}

function isoToDate(value: string | null | undefined): string {
  if (!value) return "—"
  return value.slice(0, 10)
}

function isoToDateTime(value: string | null | undefined): string {
  if (!value) return "—"
  return value.replace("T", " ").slice(0, 16)
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
